import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { guest as guestApi } from '../lib/api.js'

// --- Styles ---

const wrapper = css`
  max-width: 800px;
  margin: 0 auto;
  padding: ${space.xl} ${space.md};
`

const guestHeader = css`
  text-align: center;
  margin-bottom: ${space.xl};
`

const guestTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.sm};
`

const guestSubtitle = css`
  font-size: ${font.size.base};
  color: ${colors.textSecondary};
  line-height: 1.6;
`

const acceptForm = css`
  ${card};
  max-width: 400px;
  margin: ${space.xl} auto;
`

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  justify-content: center;
  margin-top: ${space.lg};
`

const sessionList = css`
  ${card};
  padding: 0;
  overflow: hidden;
`

const sessionRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  cursor: pointer;
  transition: background 0.1s ease;
  &:last-child { border-bottom: none; }
  &:hover { background: ${colors.bg}; }
`

const sessionTitle = css`
  flex: 1;
  font-size: ${font.size.sm};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const backLink = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  margin-bottom: ${space.lg};
  cursor: pointer;
  &:hover { color: ${colors.brand}; }
`

const chatArea = css`
  ${card};
  padding: 0;
  overflow: hidden;
`

const msgRow = css`
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const msgRole = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${space.xs};
`

const msgContent = css`
  font-size: ${font.size.sm};
  line-height: 1.6;
  white-space: pre-wrap;
`

const errorBox = css`
  ${card};
  text-align: center;
  color: ${colors.brand};
  padding: ${space.xl};
`

// --- Component ---

export function GuestTaskView() {
  let projectId = null
  let token = null
  let invite = null
  let task = null
  let state = 'loading' // loading | accept | sessions | session | error
  let errorMsg = ''
  let acceptData = { name: '', email: '' }
  let sessions = []
  let currentSession = null
  let saving = false

  async function loadInvite(pid, tok) {
    try {
      const result = await guestApi.getInvite(pid, tok)
      invite = result.invite
      task = result.task

      if (invite.accepted_at) {
        state = 'sessions'
        await loadSessions()
      } else {
        state = 'accept'
        if (invite.email) acceptData.email = invite.email
      }
    } catch (err) {
      state = 'error'
      errorMsg = err.message || 'Invalid or expired invite'
    }
    m.redraw()
  }

  async function acceptInvite() {
    if (!acceptData.name.trim()) return
    saving = true
    try {
      await guestApi.acceptInvite(projectId, token, acceptData)
      state = 'sessions'
      await loadSessions()
    } catch (err) {
      errorMsg = err.message || 'Failed to accept invite'
      state = 'error'
    } finally {
      saving = false
      m.redraw()
    }
  }

  async function loadSessions() {
    try {
      const result = await guestApi.listSessions(projectId, token)
      sessions = result.sessions || result
    } catch (err) {
      errorMsg = err.message || 'Failed to load sessions'
      state = 'error'
    }
    m.redraw()
  }

  async function openSession(sessionId) {
    try {
      currentSession = await guestApi.getSession(projectId, token, sessionId)
      state = 'session'
    } catch (err) {
      errorMsg = err.message || 'Failed to load session'
      state = 'error'
    }
    m.redraw()
  }

  function backToList() {
    currentSession = null
    state = 'sessions'
  }

  function roleBg(role) {
    if (role === 'user') return colors.msgUser
    if (role === 'assistant') return colors.msgAssistant
    return colors.msgSystem
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      token = vnode.attrs.token
      loadInvite(projectId, token)
    },

    view(vnode) {
      projectId = vnode.attrs.projectId
      token = vnode.attrs.token

      if (state === 'loading') {
        return m('div', { class: wrapper }, m('div', 'Loading...'))
      }

      if (state === 'error') {
        return m('div', { class: wrapper }, [
          m('div', { class: errorBox }, errorMsg),
        ])
      }

      if (state === 'accept') {
        return m('div', { class: wrapper }, [
          m('div', { class: guestHeader }, [
            m('h1', { class: guestTitle }, task?.title || 'Review Task'),
            task?.description
              ? m('p', { class: guestSubtitle }, task.description)
              : null,
          ]),
          m('div', { class: acceptForm }, [
            m('div', { class: formRow }, [
              m('label', { class: label }, 'Your name'),
              m('input', {
                class: input,
                value: acceptData.name,
                oninput: (e) => { acceptData.name = e.target.value },
                placeholder: 'Jane Doe',
                autofocus: true,
              }),
            ]),
            m('div', { class: formRow }, [
              m('label', { class: label }, 'Email'),
              m('input', {
                class: input,
                type: 'email',
                value: acceptData.email,
                oninput: (e) => { acceptData.email = e.target.value },
                placeholder: 'jane@example.com',
              }),
            ]),
            m('div', { class: formActions }, [
              m('button', {
                class: btnPrimary,
                disabled: saving || !acceptData.name.trim(),
                onclick: acceptInvite,
              }, saving ? 'Accepting...' : 'Accept & start'),
            ]),
          ]),
        ])
      }

      if (state === 'session' && currentSession) {
        const msgs = currentSession.messages || []
        return m('div', { class: wrapper }, [
          m('div', { class: backLink, onclick: backToList }, 'Back to sessions'),
          m('h2', {
            style: `font-size: ${font.size.lg}; font-weight: ${font.weight.semibold}; margin-bottom: ${space.md};`,
          }, currentSession.session?.title || currentSession.session?.external_id || 'Session'),
          m('div', { class: chatArea },
            msgs.map((msg, i) =>
              m('div', {
                key: msg.id || i,
                class: msgRow,
                style: `background: ${roleBg(msg.role)};`,
              }, [
                m('div', {
                  class: msgRole,
                  style: `color: ${msg.role === 'user' ? colors.brand : colors.textSecondary};`,
                }, msg.role),
                m('div', { class: msgContent }, msg.content),
              ])
            ),
          ),
        ])
      }

      // sessions list
      return m('div', { class: wrapper }, [
        m('div', { class: guestHeader }, [
          m('h1', { class: guestTitle }, task?.title || 'Review Task'),
          task?.description
            ? m('p', { class: guestSubtitle }, task.description)
            : null,
          m('p', {
            style: `font-size: ${font.size.sm}; color: ${colors.textMuted}; margin-top: ${space.sm};`,
          }, `${sessions.length} sessions to review`),
        ]),

        sessions.length === 0
          ? m('div', {
              style: `text-align: center; padding: ${space.xl}; color: ${colors.textMuted};`,
            }, 'No sessions assigned to this task.')
          : m('div', { class: sessionList },
              sessions.map(s =>
                m('div', {
                  key: s.id,
                  class: sessionRow,
                  onclick: () => openSession(s.id),
                }, [
                  m('span', { class: sessionTitle },
                    s.title || s.external_id || s.id.slice(0, 12)),
                ])
              ),
            ),
      ])
    },
  }
}
